export type ScalarRange = [number, number];

export function isFiniteScalarRange(
  range: ScalarRange | null | undefined,
  eps = 1e-12,
): range is ScalarRange {
  if (!range) return false;
  const [min, max] = range;
  return (
    Number.isFinite(min) && Number.isFinite(max) && Math.abs(max - min) > eps
  );
}

export function computeScalarRange(
  values: ArrayLike<number> | null | undefined,
): ScalarRange | null {
  if (!values || values.length === 0) return null;

  let min = Infinity;
  let max = -Infinity;

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (!Number.isFinite(value)) continue;

    if (value < min) min = value;
    if (value > max) max = value;
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  return isFiniteScalarRange([min, max]) ? [min, max] : null;
}

export function resolveScalarRange(
  values: ArrayLike<number>,
  forcedRange?: ScalarRange | null,
): ScalarRange | null {
  if (isFiniteScalarRange(forcedRange)) {
    return forcedRange;
  }
  return computeScalarRange(values);
}

export function applyPaletteToMapper(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  vtk: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapper: any,
  range: ScalarRange,
) {
  const [min, max] = range;

  let lut = mapper.getLookupTable?.();

  if (!lut) {
    lut = vtk.vtkLookupTable.newInstance();
    mapper.setLookupTable?.(lut);
  }

  lut.setNumberOfTableValues?.(256);
  lut.setHueRange?.(0.6667, 0.0);
  lut.setSaturationRange?.(1.0, 1.0);
  lut.setValueRange?.(1.0, 1.0);
  lut.setRange?.(min, max);
  lut.setMappingRange?.(min, max);
  lut.build?.();
  lut.modified?.();
  lut.forceBuild?.();

  mapper.setScalarRange(min, max);
  mapper.setLookupTable?.(lut);
  mapper.modified?.();

  return lut;
}

export function replaceScalarBar(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  vtk: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scene: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lut: any,
  label: string,
) {
  if (scene.scalarBarActor) {
    scene.renderer.removeActor2D(scene.scalarBarActor);
    scene.scalarBarActor.delete?.();
    scene.scalarBarActor = null;
  }

  if (!lut) return null;

  const scalarBarActor = vtk.vtkScalarBarActor.newInstance();
  scalarBarActor.setScalarsToColors(lut);
  scalarBarActor.setAxisLabel(label);

  scalarBarActor.setAxisTextStyle({
    fontFamily: 'Arial',
    fontColor: 'white',
    fontStyle: 'normal',
    fontWeight: 'bold',
  });

  scalarBarActor.setTickTextStyle({
    fontFamily: 'Arial',
    fontColor: 'white',
    fontStyle: 'normal',
    fontWeight: 'normal',
  });

  scalarBarActor.modified?.();

  scene.renderer.addActor2D(scalarBarActor);
  scene.scalarBarActor = scalarBarActor;

  return scalarBarActor;
}
